// src/pages/Admin/DescuentosAutomaticos/DescuentoAutomaticoDetalle.jsx
import React, { useState, useEffect } from 'react';
import {
  Card, Button, Descriptions, Tag, Space, Typography, Divider, 
  Spin, Alert, Statistic, Timeline, Empty, App, Row, Col
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, DeleteOutlined, 
  CheckCircleOutlined, CloseCircleOutlined, PercentageOutlined,
  DollarOutlined, CalendarOutlined, ThunderboltOutlined,
  AppstoreOutlined, TrophyOutlined, InfoCircleOutlined,
  ClockCircleOutlined, UserOutlined, HistoryOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import DescuentosAutomaticos from '../../../../../service/CodigosDescuento';
import { useNotify, useWithNotify } from '../../../../../providers/NotificationProvider';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Title, Text } = Typography;

const tipoColor = {
  porcentaje: '#0047bb',
  monto_fijo: '#0047bb'
};

const DescuentoAutomaticoDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const withNotify = useWithNotify();
  const { modal } = App.useApp();

  const [descuento, setDescuento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);

  // Cargar descuento
  const loadDescuento = async () => {
    try {
      setLoading(true);
      const res = await DescuentosAutomaticos.Get(id);
      setDescuento(res);
      
      // Cargar estadísticas si están disponibles
      try {
        const statsRes = await DescuentosAutomaticos.GetEstadisticas(id);
        setEstadisticas(statsRes);
      } catch (error) {

      }
    } catch (error) {
      notify.error('Error al cargar descuento automático');
      navigate('/admin/descuentos-automaticos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDescuento();
  }, [id]);

  // Verificar vigencia
  const getEstadoVigencia = () => {
    if (!descuento) return { estado: 'desconocido', color: 'default', texto: 'Desconocido' };
    
    const ahora = new Date();
    const inicio = new Date(descuento.fechaInicio);
    const fin = new Date(descuento.fechaFin);
    
    if (descuento.eliminadoEn) {
      return { estado: 'eliminado', color: 'default', texto: 'ELIMINADO' };
    }
    
    if (!descuento.activo) {
      return { estado: 'inactivo', color: 'red', texto: 'INACTIVO' };
    }
    
    if (fin < ahora) {
      return { estado: 'vencido', color: 'default', texto: 'VENCIDO' };
    }
    
    if (inicio > ahora) {
      return { estado: 'programado', color: 'processing', texto: 'PROGRAMADO' };
    }
    
    if (inicio <= ahora && fin >= ahora) {
      return { estado: 'vigente', color: 'blue', texto: 'VIGENTE' };
    }
    
    return { estado: 'desconocido', color: 'default', texto: 'Desconocido' };
  };

  // Toggle activo
  const handleToggle = async () => {
    const accion = descuento.activo ? 'desactivar' : 'activar';
    
    modal.confirm({
      title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} descuento?`,
      content: (
        <div>
          <p>¿Deseas {accion} el descuento <strong>{descuento.nombre}</strong>?</p>
          {descuento.activo ? (
            <p className="text-gray-500">El descuento dejará de aplicarse automáticamente.</p>
          ) : (
            <p className="text-gray-500">El descuento se aplicará automáticamente si está dentro del período de vigencia.</p>
          )}
        </div>
      ),
      okText: accion.charAt(0).toUpperCase() + accion.slice(1),
      okType: descuento.activo ? 'danger' : 'primary',
      cancelText: 'Cancelar',
      icon: descuento.activo ? 
        <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> : 
        <CheckCircleOutlined style={{ color: '#0047bb' }} />,
      onOk: async () => {
        try {
          await withNotify(
            DescuentosAutomaticos.ToggleActivo(id),
            {
              loading: 'Actualizando estado...',
              success: 'Estado actualizado exitosamente',
              error: 'Error al actualizar estado'
            }
          );
          loadDescuento();
        } catch (error) {
          console.error('Error al cambiar estado:', error);
        }
      }
    });
  };

  // Eliminar descuento
  const handleDelete = async () => {
    modal.confirm({
      title: '¿Eliminar descuento automático?',
      content: (
        <div>
          <p>Estás a punto de eliminar el descuento <strong>{descuento.nombre}</strong>.</p>
          <p className="text-gray-500">Esta acción marcará el descuento como eliminado y dejará de aplicarse automáticamente.</p>
        </div>
      ),
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      onOk: async () => {
        try {
          await withNotify(
            DescuentosAutomaticos.Delete(id),
            {
              loading: 'Eliminando descuento...',
              success: 'Descuento eliminado exitosamente',
              error: 'Error al eliminar descuento'
            }
          );
          navigate('/admin/descuentos-automaticos');
        } catch (error) {
          console.error('Error eliminando descuento:', error);
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Cargando descuento automático..." />
      </div>
    );
  }

  if (!descuento) {
    return (
      <div className="p-4 md:p-6">
        <Card>
          <Empty description="Descuento no encontrado" />
          <div className="text-center mt-4">
            <Button 
              type="primary"
              onClick={() => navigate('/admin/descuentos-automaticos')}
            >
              Volver
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const vigencia = getEstadoVigencia();
  const ahora = new Date();
  const inicio = new Date(descuento.fechaInicio);
  const fin = new Date(descuento.fechaFin);

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/descuentos-automaticos')}
            className="mb-4"
          >
            Volver
          </Button>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <Title level={3} className="!mb-2 flex items-center gap-2">
                <ThunderboltOutlined className="text-yellow-500" />
                {descuento.nombre}
              </Title>
              <Text type="secondary">{descuento.descripcion}</Text>
            </div>

            <Space wrap>
              <Button
                icon={<EditOutlined />}
                type="primary"
                onClick={() => navigate(`/admin/descuentos-automaticos/editar/${id}`)}
                disabled={!!descuento.eliminadoEn}
                style={{ backgroundColor: '#0047bb', borderColor: '#0047bb' }}
              >
                Editar
              </Button>
              
              <Button
                icon={descuento.activo ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                onClick={handleToggle}
                disabled={!!descuento.eliminadoEn}
                danger={descuento.activo}
                type={descuento.activo ? 'default' : 'primary'}
              >
                {descuento.activo ? 'Desactivar' : 'Activar'}
              </Button>

              {!descuento.eliminadoEn && (
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={handleDelete}
                >
                  Eliminar
                </Button>
              )}
            </Space>
          </div>
        </div>

        {/* Alert de estado */}
        {descuento.eliminadoEn && (
          <Alert
            message="Descuento Eliminado"
            description={`Este descuento fue eliminado el ${dayjs(descuento.eliminadoEn).format('DD/MM/YYYY HH:mm')} por ${descuento.eliminadoPor?.nombre || 'Usuario desconocido'}`}
            type="error"
            icon={<DeleteOutlined />}
            showIcon
            className="mb-6"
          />
        )}

        {!descuento.activo && !descuento.eliminadoEn && (
          <Alert
            message="Descuento Inactivo"
            description="Este descuento está inactivo y no se aplicará automáticamente."
            type="warning"
            icon={<CloseCircleOutlined />}
            showIcon
            className="mb-6"
          />
        )}

        {vigencia.estado === 'vigente' && (
          <Alert
            message="Descuento Vigente"
            description="Este descuento se está aplicando automáticamente en este momento."
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
            className="mb-6"
          />
        )}

        {vigencia.estado === 'programado' && (
          <Alert
            message="Descuento Programado"
            description={`Este descuento comenzará a aplicarse automáticamente el ${dayjs(inicio).format('DD/MM/YYYY HH:mm')}`}
            type="info"
            icon={<ClockCircleOutlined />}
            showIcon
            className="mb-6"
          />
        )}

        <Row gutter={[16, 16]}>
          {/* Columna izquierda - Información principal */}
          <Col xs={24} lg={16}>
            {/* Información del descuento */}
            <Card title="Información del Descuento" className="mb-4">
              <Descriptions column={{ xs: 1, sm: 2 }} bordered>
                <Descriptions.Item label="Nombre" span={2}>
                  <Text strong>{descuento.nombre}</Text>
                </Descriptions.Item>

                <Descriptions.Item label="Descripción" span={2}>
                  {descuento.descripcion}
                </Descriptions.Item>

                <Descriptions.Item label="Tipo de Descuento">
                  <Tag 
                    color={tipoColor[descuento.tipoDescuento]}
                    icon={descuento.tipoDescuento === 'porcentaje' ? <PercentageOutlined /> : <DollarOutlined />}
                  >
                    {descuento.tipoDescuento === 'porcentaje' ? 'Porcentaje' : 'Monto Fijo'}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Valor del Descuento">
                  <Text strong className="text-lg" style={{ color: tipoColor[descuento.tipoDescuento] }}>
                    {descuento.tipoDescuento === 'porcentaje' 
                      ? `${descuento.valorDescuento}%`
                      : `$${descuento.valorDescuento.toLocaleString('es-CL')}`
                    }
                  </Text>
                </Descriptions.Item>

                <Descriptions.Item label="Estado">
                  <Tag 
                    color={descuento.activo ? '#0047bb' : '#ff4d4f'}
                    icon={descuento.activo ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                  >
                    {descuento.activo ? 'ACTIVO' : 'INACTIVO'}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Vigencia">
                  <Tag color={vigencia.color} icon={<CalendarOutlined />}>
                    {vigencia.texto}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Prioridad">
                  <div className="flex items-center gap-2">
                    <TrophyOutlined style={{ 
                      color: descuento.prioridad > 5 ? '#0047bb' : descuento.prioridad > 2 ? '#0047bb' : '#8c8c8c',
                      fontSize: '18px'
                    }} />
                    <Text strong style={{ fontSize: '16px' }}>
                      {descuento.prioridad}
                    </Text>
                    <Tag color={descuento.prioridad > 5 ? 'green' : descuento.prioridad > 2 ? 'blue' : 'default'}>
                      {descuento.prioridad > 5 ? 'Alta' : descuento.prioridad > 2 ? 'Media' : 'Baja'}
                    </Tag>
                  </div>
                </Descriptions.Item>

                <Descriptions.Item label="Acumulable">
                  {descuento.acumulableConOtrosDescuentos ? (
                    <Tag color="purple" icon={<CheckCircleOutlined />}>SÍ - Puede acumularse</Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />}>NO - No acumulable</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Período de vigencia */}
            <Card 
              title={
                <span className="flex items-center gap-2">
                  <CalendarOutlined /> Período de Vigencia
                </span>
              } 
              className="mb-4"
            >
              <Timeline>
                <Timeline.Item 
                  color={inicio <= ahora ? 'green' : 'blue'}
                  dot={inicio <= ahora ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                >
                  <div>
                    <Text strong>Fecha de Inicio</Text>
                    <div className="text-gray-600">
                      {dayjs(descuento.fechaInicio).format('DD/MM/YYYY HH:mm')}
                    </div>
                    <Text type="secondary" className="text-xs">
                      {dayjs(descuento.fechaInicio).fromNow()}
                    </Text>
                  </div>
                </Timeline.Item>

                {inicio <= ahora && fin >= ahora && (
                  <Timeline.Item color="blue" dot={<ThunderboltOutlined />}>
                    <div>
                      <Tag color="blue" icon={<CheckCircleOutlined />}>
                        Activo en este momento
                      </Tag>
                      <div className="text-gray-600 text-xs mt-1">
                        El descuento se está aplicando automáticamente
                      </div>
                    </div>
                  </Timeline.Item>
                )}

                <Timeline.Item 
                  color={fin < ahora ? 'red' : 'blue'}
                  dot={fin < ahora ? <CloseCircleOutlined /> : <ClockCircleOutlined />}
                >
                  <div>
                    <Text strong>Fecha de Fin</Text>
                    <div className="text-gray-600">
                      {dayjs(descuento.fechaFin).format('DD/MM/YYYY HH:mm')}
                    </div>
                    <Text type="secondary" className="text-xs">
                      {dayjs(descuento.fechaFin).fromNow()}
                    </Text>
                  </div>
                </Timeline.Item>
              </Timeline>
            </Card>

            {/* Talleres aplicables */}
            <Card 
              title={
                <span className="flex items-center gap-2">
                  <AppstoreOutlined /> Talleres Aplicables
                </span>
              }
            >
              {descuento.talleresAplicables && descuento.talleresAplicables.length > 0 ? (
                <div className="space-y-2">
                  <Alert
                    message={`Este descuento se aplica automáticamente a ${descuento.talleresAplicables.length} taller(es)`}
                    type="info"
                    showIcon
                    className="mb-3"
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {descuento.talleresAplicables.map((taller, index) => (
                      <Card 
                        key={index} 
                        size="small" 
                        className="hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-2">
                          <CheckCircleOutlined className="text-blue-500 mt-1" />
                          <div>
                            <Text strong className="block">
                              {taller.nombre || taller}
                            </Text>
                            {taller.recinto && (
                              <Text type="secondary" className="text-xs">
                                {taller.recinto.nombre || taller.recinto}
                              </Text>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <Empty description="No hay talleres asignados" />
              )}
            </Card>
          </Col>

          {/* Columna derecha - Estadísticas y auditoría */}
          <Col xs={24} lg={8}>
            {/* Estadísticas rápidas */}
            <Card title="Resumen" className="mb-4">
              <Space direction="vertical" size="large" className="w-full">
                <Statistic
                  title="Valor del Descuento"
                  value={descuento.tipoDescuento === 'porcentaje' 
                    ? `${descuento.valorDescuento}%`
                    : `$${descuento.valorDescuento.toLocaleString('es-CL')}`
                  }
                  valueStyle={{ color: tipoColor[descuento.tipoDescuento] }}
                  prefix={descuento.tipoDescuento === 'porcentaje' ? <PercentageOutlined /> : <DollarOutlined />}
                />
                
                <Divider className="my-2" />
                
                <Statistic
                  title="Talleres Aplicables"
                  value={descuento.talleresAplicables?.length || 0}
                  prefix={<AppstoreOutlined />}
                  valueStyle={{ color: '#0047bb' }}
                />
                
                <Divider className="my-2" />
                
                <Statistic
                  title="Prioridad"
                  value={descuento.prioridad}
                  prefix={<TrophyOutlined />}
                  valueStyle={{ 
                    color: descuento.prioridad > 5 ? '#0047bb' : descuento.prioridad > 2 ? '#0047bb' : '#8c8c8c' 
                  }}
                />
              </Space>
            </Card>

            {/* Información de creación */}
            <Card 
              title={
                <span className="flex items-center gap-2">
                  <HistoryOutlined /> Auditoría
                </span>
              }
              className="mb-4"
            >
              <Space direction="vertical" size="small" className="w-full">
                <div>
                  <Text type="secondary" className="text-xs">Creado por</Text>
                  <div className="flex items-center gap-2 mt-1">
                    <UserOutlined />
                    <Text strong>{descuento.creadoPor?.nombre || 'Usuario desconocido'}</Text>
                  </div>
                  <Text type="secondary" className="text-xs block mt-1">
                    {dayjs(descuento.creadoEn).format('DD/MM/YYYY HH:mm')}
                  </Text>
                  <Text type="secondary" className="text-xs block">
                    {dayjs(descuento.creadoEn).fromNow()}
                  </Text>
                </div>

                {descuento.modificadoPor && (
                  <>
                    <Divider className="my-2" />
                    <div>
                      <Text type="secondary" className="text-xs">Última modificación</Text>
                      <div className="flex items-center gap-2 mt-1">
                        <UserOutlined />
                        <Text strong>{descuento.modificadoPor?.nombre || 'Usuario desconocido'}</Text>
                      </div>
                      <Text type="secondary" className="text-xs block mt-1">
                        {dayjs(descuento.modificadoEn).format('DD/MM/YYYY HH:mm')}
                      </Text>
                      <Text type="secondary" className="text-xs block">
                        {dayjs(descuento.modificadoEn).fromNow()}
                      </Text>
                    </div>
                  </>
                )}

                {descuento.eliminadoPor && (
                  <>
                    <Divider className="my-2" />
                    <div>
                      <Text type="secondary" className="text-xs">Eliminado por</Text>
                      <div className="flex items-center gap-2 mt-1">
                        <UserOutlined />
                        <Text strong>{descuento.eliminadoPor?.nombre || 'Usuario desconocido'}</Text>
                      </div>
                      <Text type="secondary" className="text-xs block mt-1">
                        {dayjs(descuento.eliminadoEn).format('DD/MM/YYYY HH:mm')}
                      </Text>
                    </div>
                  </>
                )}
              </Space>
            </Card>

            {/* Cómo funciona */}
            <Card 
              title={
                <span className="flex items-center gap-2">
                  <InfoCircleOutlined /> Cómo Funciona
                </span>
              }
              className="bg-blue-50"
            >
              <Space direction="vertical" size="small">
                <div className="flex items-start gap-2">
                  <ThunderboltOutlined className="text-yellow-500 mt-1" />
                  <Text className="text-xs">
                    Se aplica <strong>automáticamente</strong> durante el período configurado
                  </Text>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircleOutlined className="text-blue-500 mt-1" />
                  <Text className="text-xs">
                    No requiere código promocional
                  </Text>
                </div>
                <div className="flex items-start gap-2">
                  <TrophyOutlined className="text-blue-500 mt-1" />
                  <Text className="text-xs">
                    Prioridad <strong>{descuento.prioridad}</strong> - Se aplica {
                      descuento.prioridad > 5 ? 'primero' : 
                      descuento.prioridad > 2 ? 'en orden medio' : 
                      'al final'
                    }
                  </Text>
                </div>
                <div className="flex items-start gap-2">
                  {descuento.acumulableConOtrosDescuentos ? (
                    <>
                      <CheckCircleOutlined className="text-purple-500 mt-1" />
                      <Text className="text-xs">
                        Puede <strong>acumularse</strong> con otros descuentos
                      </Text>
                    </>
                  ) : (
                    <>
                      <CloseCircleOutlined className="text-gray-500 mt-1" />
                      <Text className="text-xs">
                        <strong>No acumulable</strong> - Se aplica solo este descuento
                      </Text>
                    </>
                  )}
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DescuentoAutomaticoDetalle;
